import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Chart } from "chart.js/auto";
import { useAppContext } from "components/Context/AppContext";
import DashboardTopbar from "components/DashboardTopbar";
import ExpandIcon from "components/ExpandIcon";
import DashboardMenuBar from "components/DashboardMenuBar";
import CardTools, { ExpandModal } from "components/CardTools";
import { downloadCsv } from "utils/downloadCsv";
import { fmtDec } from "utils/format";
import "styles/dashboard.css";
import "./alternatives.css";

const DASHBOARD_API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";


const COLORDER_KEY = "trg-alt-colorder";

const ALL_COLUMNS = [
  { key: "ticker",          label: "Security ID",    width:  90, type: "text", align: "left"  },
  { key: "name",            label: "Security Name",  width: 220, type: "text", align: "left"  },
  { key: "class",           label: "Class",          width:  95, type: "text", align: "left"  },
  { key: "sc1",             label: "SC1",            width: 130, type: "text", align: "left"  },
  { key: "region",          label: "Region",         width: 110, type: "text", align: "left"  },
  { key: "country",         label: "Country",        width: 110, type: "text", align: "left"  },
  { key: "industry",        label: "Industry",       width: 220, type: "text", align: "left"  },
  { key: "currency",        label: "Currency",       width:  70, type: "text", align: "left"  },
  { key: "market_value",    label: "Market Value",   width: 105, type: "num",  align: "right",
    fmt: (v) => Math.round(Number(v)).toLocaleString() },
  { key: "weight",          label: "MV Weight",      width:  80, type: "num",  align: "right",
    fmt: (v) => fmtDec(Number(v)) },
  { key: "unrealized_gain", label: "Unrealized Gain",width: 120, type: "num",  align: "right",
    fmt: (v) => { const n = Number(v); return (n < 0 ? "-" : "+") + "$" + Math.round(Math.abs(n)).toLocaleString(); },
    color: (v) => (Number(v) < 0 ? "#e05252" : "#22d47e") },
  { key: "ytd_return",      label: "YTD Return",     width:  95, type: "num",  align: "right",
    fmt: (v) => { const n = Number(v); return (n < 0 ? "" : "+") + fmtDec(n * 100) + "%"; },
    color: (v) => (Number(v) < 0 ? "#e05252" : "#22d47e") },
  { key: "proxy_name",      label: "Proxy Index",    width: 180, type: "text", align: "left"  },
  { key: "proxy_correl",    label: "Corr. to Proxy", width:  95, type: "num",  align: "right",
    fmt: (v) => fmtDec(Number(v) * 100) + "%" },
  { key: "unadj_vol",       label: "Unadj. Vol",     width:  85, type: "num",  align: "right",
    fmt: (v) => fmtDec(Number(v) * 100) + "%" },
  { key: "adj_vol",         label: "Liq. Adj. Vol",  width:  95, type: "num",  align: "right",
    fmt: (v) => fmtDec(Number(v) * 100) + "%" },
  { key: "ret_unadj",       label: "Unadj. Exp Ret", width:  95, type: "num",  align: "right",
    fmt: (v) => fmtDec(Number(v)) },
  { key: "ret_adj",         label: "Adj. Exp Ret",   width:  95, type: "num",  align: "right",
    fmt: (v) => (v == null || isNaN(Number(v))) ? "—" : fmtDec(Number(v)) },
];
const DEFAULT_ORDER = ALL_COLUMNS.map((c) => c.key);

function loadColOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLORDER_KEY) || "null");
    if (!Array.isArray(saved)) return DEFAULT_ORDER.slice();
    const valid   = saved.filter((k) => ALL_COLUMNS.some((c) => c.key === k));
    const missing = DEFAULT_ORDER.filter((k) => !valid.includes(k));
    return valid.concat(missing);
  } catch { return DEFAULT_ORDER.slice(); }
}
function saveColOrder(order) {
  try { localStorage.setItem(COLORDER_KEY, JSON.stringify(order)); } catch { /* noop */ }
}

// ── Colour helpers ─────────────────────────────────────────────────────────────
function lerpCol(a, b, t) {
  const h = (s) => parseInt(s.replace("#", ""), 16);
  const ch = (ca, cb, i) =>
    Math.round(((h(ca) >> i) & 255) + (((h(cb) >> i) & 255) - ((h(ca) >> i) & 255)) * t)
      .toString(16).padStart(2, "0");
  return "#" + ch(a, b, 16) + ch(a, b, 8) + ch(a, b, 0);
}

// ── Dual-needle SVG Gauge ─────────────────────────────────────────────────────
function DualNeedleGauge({ min = 0, max, value, illiqValue, limit, target, targetLabel, colourFn }) {
  const svgRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const CX = 140, CY = 148, R = 105, RI = 91, N = 120;
    const toAngle = (v) => Math.PI + ((Math.min(Math.max(v, min), max) - min) / (max - min)) * Math.PI;

    // Build arc segments
    const arcGroup = svg.querySelector(".alt-g-arc");
    if (arcGroup) {
      arcGroup.innerHTML = "";
      const NS = "http://www.w3.org/2000/svg";
      for (let i = 0; i < N; i++) {
        const t0 = Math.PI + (i / N) * Math.PI;
        const t1 = Math.PI + ((i + 1) / N) * Math.PI;
        const v  = min + (i / N) * (max - min);
        const x1o = CX + Math.cos(t0) * R,   y1o = CY + Math.sin(t0) * R;
        const x2o = CX + Math.cos(t1) * R,   y2o = CY + Math.sin(t1) * R;
        const x1i = CX + Math.cos(t0) * RI,  y1i = CY + Math.sin(t0) * RI;
        const x2i = CX + Math.cos(t1) * RI,  y2i = CY + Math.sin(t1) * RI;
        const p = document.createElementNS(NS, "path");
        p.setAttribute("d",
          `M${x1i.toFixed(1)},${y1i.toFixed(1)} L${x1o.toFixed(1)},${y1o.toFixed(1)} ` +
          `A${R},${R} 0 0,1 ${x2o.toFixed(1)},${y2o.toFixed(1)} ` +
          `L${x2i.toFixed(1)},${y2i.toFixed(1)} ` +
          `A${RI},${RI} 0 0,0 ${x1i.toFixed(1)},${y1i.toFixed(1)} Z`
        );
        p.setAttribute("fill", colourFn(v));
        p.setAttribute("opacity", "0.88");
        arcGroup.appendChild(p);
      }
    }

    // Needle 1 — unadjusted (blue, shorter)
    const a1 = toAngle(value);
    const n1El = svg.querySelector(".alt-g-n1");
    const t1El = svg.querySelector(".alt-g-t1");
    if (n1El) { n1El.setAttribute("x2", (CX + Math.cos(a1) * 78).toFixed(2)); n1El.setAttribute("y2", (CY + Math.sin(a1) * 78).toFixed(2)); }
    if (t1El) { t1El.setAttribute("cx", (CX + Math.cos(a1) * 78).toFixed(2)); t1El.setAttribute("cy", (CY + Math.sin(a1) * 78).toFixed(2)); }

    // Needle 2 — illiq adjusted (gold, longer)
    const a2 = toAngle(illiqValue);
    const n2El = svg.querySelector(".alt-g-n2");
    const t2El = svg.querySelector(".alt-g-t2");
    if (n2El) { n2El.setAttribute("x2", (CX + Math.cos(a2) * 92).toFixed(2)); n2El.setAttribute("y2", (CY + Math.sin(a2) * 92).toFixed(2)); }
    if (t2El) { t2El.setAttribute("cx", (CX + Math.cos(a2) * 92).toFixed(2)); t2El.setAttribute("cy", (CY + Math.sin(a2) * 92).toFixed(2)); }

    // Target/limit tick
    const tgtVal = limit ?? target;
    const ta = toAngle(tgtVal);
    const tickEl = svg.querySelector(".alt-g-tick");
    if (tickEl) {
      tickEl.setAttribute("x1", (CX + Math.cos(ta) * 116).toFixed(1));
      tickEl.setAttribute("y1", (CY + Math.sin(ta) * 116).toFixed(1));
      tickEl.setAttribute("x2", (CX + Math.cos(ta) * 88).toFixed(1));
      tickEl.setAttribute("y2", (CY + Math.sin(ta) * 88).toFixed(1));
    }
    const tgtLblEl = svg.querySelector(".alt-g-tgtlbl");
    if (tgtLblEl) {
      tgtLblEl.setAttribute("x", (CX + Math.cos(ta) * 126).toFixed(1));
      tgtLblEl.setAttribute("y", (CY + Math.sin(ta) * 126 + 3).toFixed(1));
      tgtLblEl.textContent = targetLabel;
    }
    // Lo/hi labels
    const lblLo = svg.querySelector(".alt-g-lbllo");
    const lblHi = svg.querySelector(".alt-g-lblhi");
    if (lblLo) { lblLo.setAttribute("x", "35"); lblLo.setAttribute("y", "162"); lblLo.textContent = String(min); }
    if (lblHi) { lblHi.setAttribute("x", "245"); lblHi.setAttribute("y", "162"); lblHi.textContent = max == null ? "" : (typeof max === "number" && max < 2 ? max.toFixed(2) : max.toFixed(0) + (limit !== undefined ? "M" : "")); }
  }, [min, max, value, illiqValue, limit, target, targetLabel, colourFn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <svg ref={svgRef} viewBox="0 0 280 166"
      style={{ display: "block", width: "100%", maxWidth: 280, overflow: "hidden" }}>
      <g className="alt-g-arc" />
      <line className="alt-g-n1" x1="140" y1="148" x2="140" y2="70"
        stroke="#4e8fdd" strokeWidth="2" strokeLinecap="round" />
      <line className="alt-g-n2" x1="140" y1="148" x2="140" y2="56"
        stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" />
      <circle className="alt-g-t1" cx="140" cy="70" r="3.5" fill="#4e8fdd" />
      <circle className="alt-g-t2" cx="140" cy="56" r="4"   fill="#C9A84C" />
      <circle cx="140" cy="148" r="6"   fill="var(--dash-surface)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
      <circle cx="140" cy="148" r="2.5" fill="rgba(255,255,255,0.6)" />
      <line className="alt-g-tick" x1="0" y1="0" x2="0" y2="0"
        stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="4 3" />
      <text className="alt-g-tgtlbl" fontSize="9" fill="rgba(255,255,255,0.45)" textAnchor="middle" />
      <text className="alt-g-lbllo"  fontSize="10" fontWeight="600" fill="var(--dash-muted)" textAnchor="middle" />
      <text className="alt-g-lblhi"  fontSize="10" fontWeight="600" fill="var(--dash-muted)" textAnchor="middle" />
    </svg>
  );
}

// ── Drillable donut chart ──────────────────────────────────────────────────────
function DrillDonut({ id, subclasses, drill, mode, theme, drillKey, onDrill }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  const isLight = theme === "light";

  function getChartData(dk) {
    if (dk === "root") {
      return subclasses.map((s) => ({ name: s.name, value: mode === "alloc" ? s.alloc : s.varc, color: s.color }));
    }
    const parent = subclasses.find((s) => s.name === dk);
    return (drill[dk] || []).map((p, i) => ({
      name:  p.name,
      value: mode === "alloc" ? p.alloc : p.varc,
      color: parent ? lerpCol(parent.color, "#ffffff", i * 0.15) : "#888",
    }));
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const chartData = getChartData(drillKey);
    if (!chartData.length) return;

    const existing = Chart.getChart(canvas);
    if (existing) existing.destroy();

    chartRef.current = new Chart(canvas.getContext("2d"), {
      type: "doughnut",
      data: {
        labels: chartData.map((d) => d.name),
        datasets: [{
          data:            chartData.map((d) => d.value),
          backgroundColor: chartData.map((d) => d.color + "cc"),
          borderColor:     chartData.map((d) => d.color),
          borderWidth: 1.5,
          hoverBorderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: "62%",
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isLight ? "rgba(255,255,255,0.97)" : "rgba(8,14,24,0.96)",
            borderColor:     isLight ? "rgba(0,0,0,0.12)"       : "rgba(255,255,255,0.08)",
            borderWidth: 1, padding: 8,
            titleColor: isLight ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)",
            bodyColor:  isLight ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.85)",
            callbacks: { label: (ctx) => ` ${ctx.label}: ${fmtDec(ctx.raw)}%` },
          },
        },
        onClick: (_, els) => {
          if (!els.length || drillKey !== "root") return;
          const label = chartData[els[0].index]?.name;
          const drillName = Object.keys(drill).find((k) => k === label || label?.startsWith(k));
          if (drillName) onDrill(drillName);
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [drillKey, subclasses, drill, mode, isLight]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!subclasses?.length) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 120, color: "var(--dash-muted)", fontSize: 13 }}>
        No alternative positions
      </div>
    );
  }

  const currentData = getChartData(drillKey);
  const legendData = drillKey === "root" ? subclasses.map((s) => ({
    name: s.name, color: s.color, pct: fmtDec(mode === "alloc" ? s.alloc : s.varc),
  })) : currentData.map((d) => ({ name: d.name, color: d.color, pct: fmtDec(d.value) }));

  return (
    <>
      {drillKey !== "root" && (
        <div className="alt-breadcrumb" style={{ marginBottom: "0.3rem" }}>
          <span className="alt-bc-link" onClick={() => onDrill("root")}>All</span>
          <span> › </span>
          <span>{drillKey}</span>
        </div>
      )}
      <div className="alt-donut-canvas-wrap">
        <canvas ref={canvasRef} />
      </div>
      <div className="alt-donut-legend">
        {legendData.map((d) => (
          <div key={d.name} className="alt-legend-item"
            style={drillKey === "root" && drill[d.name] ? { cursor: "pointer" } : {}}
            onClick={() => { if (drillKey === "root" && drill[d.name]) onDrill(d.name); }}>
            <div className="alt-legend-dot" style={{ background: d.color }} />
            <span className="alt-legend-label">{d.name}</span>
            <span className="alt-legend-pct">{d.pct}%</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Stacked area historical chart (canvas, mirrors prototype exactly) ─────────
function setupHiDPI(canvas, height) {
  const dpr = window.devicePixelRatio || 1;
  const W   = canvas.parentElement.clientWidth || 600;
  const H   = height;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width  = W + "px";
  canvas.style.height = H + "px";
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, W, H };
}

function HistChart({ history, theme }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !history) return;

    function draw() {

    const isLight   = theme === "light";
    const gridCol   = () => isLight ? "rgba(0,0,0,0.06)"       : "rgba(255,255,255,0.05)";
    const textCol   = (a) => isLight ? `rgba(0,0,0,${a})`      : `rgba(255,255,255,${a})`;
    const GOLD      = "#C9A84C";
    const FONT_M    = '"DM Mono"';
    const FONT_B    = "Outfit";

    const padTop = 20, padBot = 30, padL = 50, padR = 18;
    const targetH = 280;
    const { ctx, W } = setupHiDPI(canvas, targetH);
    const chartW = W - padL - padR;
    const chartH = targetH - padTop - padBot;

    const HIST_MONTHS  = history.labels;
    const HIST_SERIES  = history.series;
    const N = HIST_MONTHS.length;
    if (!N || !HIST_SERIES.length) {
      const { ctx, W } = setupHiDPI(canvas, 280);
      ctx.clearRect(0, 0, W, 280);
      ctx.font      = `500 13px ${FONT_B}`;
      ctx.fillStyle = textCol(0.3);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("No alternative positions", W / 2, 140);
      return;
    }

    // Scale based on last date's total raw market value
    const lastRawTotal = HIST_SERIES.reduce((sum, s) => sum + s.values[N - 1], 0);
    const useK    = lastRawTotal < 1_500_000;
    const divisor = useK ? 1_000 : 1_000_000;
    const unit    = useK ? "K" : "M";

    const scaledSeries = HIST_SERIES.map(s => ({
      ...s,
      values: s.values.map(v => v / divisor),
    }));

    // Cumulative totals per month (scaled)
    const totals = HIST_MONTHS.map((_, i) =>
      scaledSeries.reduce((sum, s) => sum + s.values[i], 0)
    );
    const maxV = Math.max(...totals);

    // Nice y-axis step
    const niceStep = maxV > 1200 ? 200 : maxV > 600 ? 100 : maxV > 300 ? 50 : maxV > 100 ? 25 : maxV > 40 ? 10 : maxV > 20 ? 5 : maxV > 8 ? 2 : 1;
    const yTop   = Math.ceil(maxV / niceStep) * niceStep;
    const yRange = yTop;

    const xAt = (i) => padL + (i / (N - 1)) * chartW;
    const yAt = (v) => padTop + (1 - v / yRange) * chartH;

    // Y-axis grid + labels
    ctx.strokeStyle = gridCol();
    ctx.fillStyle   = textCol(0.4);
    ctx.font        = `500 9px ${FONT_M}`;
    ctx.lineWidth   = 1;
    ctx.textAlign   = "right";
    ctx.textBaseline = "middle";
    const yTicks = Math.round(yRange / niceStep);
    for (let i = 0; i <= yTicks; i++) {
      const v = i * niceStep;
      const y = yAt(v);
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillText(v.toFixed(0), padL - 6, y);
    }

    // X-axis labels (every 3 months + last)
    ctx.textAlign    = "center";
    ctx.textBaseline = "top";
    ctx.font         = `500 9px ${FONT_B}`;
    ctx.fillStyle    = textCol(0.45);
    for (let i = 0; i < N; i++) {
      if (i % 3 !== 0 && i !== N - 1) continue;
      ctx.fillText(HIST_MONTHS[i], xAt(i), padTop + chartH + 6);
    }

    // rgba helper
    function rgba(hex, a) {
      const h = hex.replace("#", "");
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    }

    // Build cumulative band boundaries
    const cum = [];
    scaledSeries.forEach((s, k) => {
      cum[k] = HIST_MONTHS.map((_, i) => (k === 0 ? 0 : cum[k - 1][i]) + s.values[i]);
    });

    // Draw stacked filled bands
    scaledSeries.forEach((s, k) => {
      const lower = k === 0 ? HIST_MONTHS.map(() => 0) : cum[k - 1];
      const upper = cum[k];
      ctx.beginPath();
      upper.forEach((v, i) => { const x = xAt(i), y = yAt(v); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
      for (let i = N - 1; i >= 0; i--) ctx.lineTo(xAt(i), yAt(lower[i]));
      ctx.closePath();
      ctx.fillStyle = rgba(s.color, 0.42);
      ctx.fill();
      // Upper edge line
      ctx.beginPath();
      upper.forEach((v, i) => { const x = xAt(i), y = yAt(v); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
      ctx.strokeStyle = s.color;
      ctx.lineWidth   = 1.5;
      ctx.lineJoin    = "round";
      ctx.stroke();
    });

    // Total line (gold, on top of stack)
    ctx.beginPath();
    totals.forEach((v, i) => { const x = xAt(i), y = yAt(v); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.strokeStyle = GOLD;
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.stroke();

    // Dot + label at last point
    const lx = xAt(N - 1), ly = yAt(totals[N - 1]);
    ctx.fillStyle = GOLD;
    ctx.beginPath(); ctx.arc(lx, ly, 3.5, 0, Math.PI * 2); ctx.fill();
    ctx.font          = `600 10px ${FONT_M}`;
    ctx.fillStyle     = GOLD;
    ctx.textAlign     = "right";
    ctx.textBaseline  = "bottom";
    ctx.fillText(totals[N - 1].toFixed(useK ? 0 : 1) + unit, lx - 2, ly - 5);
    }

    draw();
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, [history, theme]);

  return <canvas ref={canvasRef} style={{ display: "block", width: "100%" }} />;
}

// ── Positions table ───────────────────────────────────────────────────────────
function PositionsTable({ positions, drillFilter }) {
  const [colOrder, setColOrder] = useState(loadColOrder);
  const [sortKey,  setSortKey]  = useState("sc1");
  const [sortDir,  setSortDir]  = useState("asc");
  const dragKey = useRef(null);

  function orderedCols() {
    return colOrder.map((k) => ALL_COLUMNS.find((c) => c.key === k)).filter(Boolean);
  }

  function handleSort(col) {
    if (sortKey === col.key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(col.key); setSortDir(col.type === "num" ? "desc" : "asc"); }
  }

  function handleDrop(fromKey, toKey) {
    if (fromKey === toKey) return;
    const next = [...colOrder];
    const from = next.indexOf(fromKey), to = next.indexOf(toKey);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, fromKey);
    setColOrder(next);
    saveColOrder(next);
  }

  const cols = orderedCols();

  let rows = drillFilter
    ? positions.filter((r) => {
        const drillSubclasses = drillFilter === "Real Estate"
          ? ["Real Estate", "Real Estate/REITs"]
          : [drillFilter];
        return drillSubclasses.includes(r.sc1);
      })
    : positions.slice();

  // Sort
  const sortCol = ALL_COLUMNS.find((c) => c.key === sortKey);
  if (sortCol) {
    const dir = sortDir === "asc" ? 1 : -1;
    rows = rows.slice().sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (sortCol.type === "num" ? Number(av) - Number(bv) : String(av).localeCompare(String(bv))) * dir;
    });
  }

  // Group by sc1
  const groups = [];
  const gi = {};
  rows.forEach((r) => {
    if (!(r.sc1 in gi)) { gi[r.sc1] = groups.length; groups.push({ sc1: r.sc1, items: [] }); }
    groups[gi[r.sc1]].items.push(r);
  });

  const gainCol = ALL_COLUMNS.find((c) => c.key === "unrealized_gain");
  const ytdCol  = ALL_COLUMNS.find((c) => c.key === "ytd_return");

  return (
    <div className="alt-table-wrap">
      <table className="alt-table">
        <thead>
          <tr>
            {cols.map((col) => (
              <th
                key={col.key}
                style={{ textAlign: col.align, minWidth: col.width }}
                draggable
                title="Click to sort · drag to reorder"
                onClick={() => handleSort(col)}
                onDragStart={(e) => { dragKey.current = col.key; e.dataTransfer.effectAllowed = "move"; e.currentTarget.classList.add("alt-dragging"); }}
                onDragEnd={(e) => { e.currentTarget.classList.remove("alt-dragging"); document.querySelectorAll("th.alt-drop-target").forEach((t) => t.classList.remove("alt-drop-target")); }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("alt-drop-target"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("alt-drop-target")}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("alt-drop-target"); if (dragKey.current) handleDrop(dragKey.current, col.key); }}
              >
                <span className="alt-drag-grip" aria-hidden>⋮⋮</span>
                {col.label}
                <span className="alt-sort-ind">
                  {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const sumGain = g.items.reduce((s, r) => s + Number(r.unrealized_gain || 0), 0);
            const sumMv   = g.items.reduce((s, r) => s + Number(r.market_value   || 0), 0);
            const wYtd    = sumMv > 0 ? g.items.reduce((s, r) => s + Number(r.ytd_return || 0) * Number(r.market_value || 0), 0) / sumMv : 0;
            return [
              <tr key={"g-" + g.sc1} className="alt-group-row">
                {cols.map((col, i) => {
                  if (i === 0) return (
                    <td key={col.key} style={{ textAlign: "left", fontWeight: 700, color: "var(--dash-gold-text)", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                      {g.sc1} <span style={{ fontWeight: 500, color: "var(--dash-muted2)" }}>({g.items.length})</span>
                    </td>
                  );
                  if (col.key === "unrealized_gain") {
                    const clr = gainCol.color?.(sumGain) || "";
                    return <td key={col.key} style={{ textAlign: col.align, fontWeight: 700, color: clr || undefined }}>{gainCol.fmt(sumGain)}</td>;
                  }
                  if (col.key === "ytd_return") {
                    const clr = ytdCol.color?.(wYtd) || "";
                    return <td key={col.key} style={{ textAlign: col.align, fontWeight: 700, color: clr || undefined }}>{ytdCol.fmt(wYtd)}</td>;
                  }
                  return <td key={col.key} />;
                })}
              </tr>,
              ...g.items.map((r) => (
                <tr key={r.ticker}>
                  {cols.map((col) => {
                    const v = r[col.key];
                    const disp = v == null ? "" : col.fmt ? col.fmt(v) : v;
                    const clr  = v != null && col.color ? col.color(v) : "";
                    return (
                      <td key={col.key} style={{ textAlign: col.align, color: clr || undefined, fontWeight: clr ? 600 : undefined }}>
                        {disp}
                      </td>
                    );
                  })}
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function AlternativesPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]   = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [data,  setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [fsPanel, setFsPanel] = useState(null); // { title, content }
  const [drillKey, setDrillKey] = useState("root");
  const [positionsExpandOpen, setPositionsExpandOpen] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;
    setLoading(true);
    fetch(`${DASHBOARD_API}/alternatives/summary?token=${token}&account_id=${aid}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setDrillKey("root"); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  const handleTheme = useCallback((t) => setTheme(t), []);

  // Gauge colour functions
  function riskColFn(gauges) {
    const { unadj_var, liqadj_var, limit, max, band } = gauges.var;
    const divisor = max < 1_500_000 ? 1_000 : 1_000_000;
    const unit    = max < 1_500_000 ? 'K' : 'M';
    const sc      = (v) => v != null ? v / divisor : null;
    const sMax = sc(max), sLimit = sc(limit), sBand = sc(band);
    return {
      varUnadj: sc(unadj_var),
      varIlliq: sc(liqadj_var),
      limit: sLimit,
      max:   sMax,
      unit,
      colourFn: (v) => {
        const p = v / sMax, pY = (sLimit - sBand) / sMax, pR = (sLimit + sBand) / sMax;
        if (p <= pY) return lerpCol("#22d47e", "#C9A84C", Math.min(1, p / pY));
        if (p <= pR) return lerpCol("#C9A84C", "#e05252", (p - pY) / (pR - pY));
        return "#e05252";
      },
    };
  }

  function sharpeColFn(gauges) {
    const { unadj_ratio, liqadj_ratio, target, max, band } = gauges.sharpe;
    return {
      sharpeUnadj: unadj_ratio,
      sharpeIlliq: liqadj_ratio,
      max,
      colourFn: (v) => {
        const p = v / max, tp = target / max;
        if (p < tp - band) return lerpCol("#e05252", "#C9A84C", Math.min(1, p / (tp - band)));
        if (p < tp + band) return lerpCol("#C9A84C", "#22d47e", (p - (tp - band)) / (band * 2));
        return "#22d47e";
      },
    };
  }

  const risk   = data?.gauges ? riskColFn(data.gauges)   : null;
  const sharpe = data?.gauges ? sharpeColFn(data.gauges) : null;

  const openFs = (title, content) => setFsPanel({ title, content });
  const closeFs = () => setFsPanel(null);

  const handlePositionsCsv = () => {
    const rows = data?.positions
      ? (drillKey !== "root"
          ? data.positions.filter((r) => {
              const subs = drillKey === "Real Estate"
                ? ["Real Estate", "Real Estate/REITs"]
                : [drillKey];
              return subs.includes(r.sc1);
            })
          : data.positions)
      : [];
    downloadCsv(
      "alternative_positions",
      ALL_COLUMNS.map((c) => ({
        label: c.label,
        accessor: (r) => r[c.key],
        fmt: (v) => (v == null ? "" : c.fmt ? c.fmt(v) : v),
      })),
      rows
    );
  };

  const positionsTable = data?.positions
    ? <PositionsTable positions={data.positions} drillFilter={drillKey !== "root" ? drillKey : null} />
    : !loading && <div className="trg-no-data" style={{ padding: "1rem" }}>No data</div>;

  return (
    <div className={`trg-shell alt-shell${theme === "light" ? " theme-light" : ""}`}>
      <Helmet><title>Alternatives — Tail Risk Global</title></Helmet>
      <DashboardTopbar />
      <DashboardMenuBar onThemeChange={handleTheme} />

      <main className="alt-body">

        {/* ── Historical MV chart ── */}
        <div className="trg-card">
          <div className="trg-card-header" style={{ paddingRight: "2.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
              <div className="trg-card-title">Historical Market Value</div>
              <div className="alt-hist-subtitle">By alternatives sub-class · $M</div>
            </div>
            {data?.history && (
              <div className="alt-hist-legend">
                {data.history.series.map((s) => (
                  <div key={s.name} className="alt-hist-legend-item">
                    <div className="alt-hist-swatch" style={{ background: s.color }} />
                    {s.name}
                  </div>
                ))}
                <div className="alt-hist-legend-item">
                  <div style={{ width: 14, height: 2.5, borderRadius: 2, background: "#C9A84C", flexShrink: 0 }} />
                  Total
                </div>
              </div>
            )}
            <button className="trg-expand-btn" title="Expand"
              onClick={() => data?.history && openFs("Historical Market Value",
                <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                  <HistChart history={data.history} theme={theme} />
                </div>
              )}><ExpandIcon /></button>
          </div>
          <div className="alt-chart-wrap">
            {data?.history
              ? <HistChart history={data.history} theme={theme} />
              : !loading && <div className="trg-no-data">No data</div>
            }
          </div>
        </div>

        {/* ── 4-column row: gauges + donuts ── */}
        <div className="alt-donut-row">

          {/* Risk vs Limit */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Risk vs Limit</div>
            </div>
            {risk?.max != null ? (
              <div className="alt-gauge-body">
                <DualNeedleGauge
                  min={0} max={risk.max}
                  value={risk.varUnadj} illiqValue={risk.varIlliq}
                  limit={risk.limit}
                  targetLabel={`Limit ${risk.limit?.toFixed(0)}${risk.unit}`}
                  colourFn={risk.colourFn}
                />
                <div className="alt-gauge-readings">
                  <div style={{ textAlign: "center" }}>
                    <div className="alt-gauge-read-label">Unadj. VaR</div>
                    <div className="alt-gauge-read-val" style={{ color: "#4e8fdd" }}>
                      {risk.varUnadj?.toFixed(1)}{risk.unit}
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="alt-gauge-read-label">Illiq. Adj. VaR</div>
                    <div className="alt-gauge-read-val" style={{ color: "#C9A84C" }}>
                      {risk.varIlliq?.toFixed(1)}{risk.unit}
                    </div>
                  </div>
                </div>
              </div>
            ) : !loading && <div className="trg-no-data">No data</div>}
          </div>

          {/* Sharpe Ratio vs BMK */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Sharpe Ratio vs BMK</div>
            </div>
            {sharpe?.max != null ? (
              <div className="alt-gauge-body">
                <DualNeedleGauge
                  min={0} max={sharpe.max}
                  value={sharpe.sharpeUnadj} illiqValue={sharpe.sharpeIlliq}
                  target={data.gauges.sharpe.target}
                  targetLabel={`Target ${fmtDec(Number(data.gauges.sharpe.target))}`}
                  colourFn={sharpe.colourFn}
                />
                <div className="alt-gauge-readings">
                  <div style={{ textAlign: "center" }}>
                    <div className="alt-gauge-read-label">Unadj. ratio</div>
                    <div className="alt-gauge-read-val" style={{ color: "#4e8fdd" }}>
                      {sharpe.sharpeUnadj != null ? fmtDec(sharpe.sharpeUnadj) : '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="alt-gauge-read-label">Illiq. adj. ratio</div>
                    <div className="alt-gauge-read-val" style={{ color: "#C9A84C" }}>
                      {sharpe.sharpeIlliq != null ? fmtDec(sharpe.sharpeIlliq) : '—'}
                    </div>
                  </div>
                </div>
              </div>
            ) : !loading && <div className="trg-no-data">No data</div>}
          </div>

          {/* Allocation Breakdown donut */}
          <div className="trg-card">
            <div className="trg-card-header" style={{ paddingRight: "2.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                <div className="trg-card-title" style={{ whiteSpace: "normal", lineHeight: 1.25 }}>
                  Allocation<br />Breakdown
                </div>
              </div>
              <button className="trg-expand-btn" title="Expand"
                onClick={() => data?.subclasses && openFs("Allocation Breakdown",
                  <div className="alt-donut-body" style={{ flex: 1 }}>
                    <DrillDonut id="alloc-fs" subclasses={data.subclasses} drill={data.drill} mode="alloc" theme={theme} drillKey={drillKey} onDrill={setDrillKey} />
                  </div>
                )}><ExpandIcon /></button>
            </div>
            <div className="alt-donut-body">
              {data?.subclasses
                ? <DrillDonut id="alloc" subclasses={data.subclasses} drill={data.drill} mode="alloc" theme={theme} drillKey={drillKey} onDrill={setDrillKey} />
                : !loading && <div className="trg-no-data">No data</div>}
            </div>
          </div>

          {/* VaR Contribution donut */}
          <div className="trg-card">
            <div className="trg-card-header" style={{ paddingRight: "2.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                <div className="trg-card-title" style={{ whiteSpace: "normal", lineHeight: 1.25 }}>
                  VaR Contribution<br />Breakdown
                </div>
              </div>
              <button className="trg-expand-btn" title="Expand"
                onClick={() => data?.subclasses && openFs("VaR Contribution Breakdown",
                  <div className="alt-donut-body" style={{ flex: 1 }}>
                    <DrillDonut id="varc-fs" subclasses={data.subclasses} drill={data.drill} mode="varc" theme={theme} drillKey={drillKey} onDrill={setDrillKey} />
                  </div>
                )}><ExpandIcon /></button>
            </div>
            <div className="alt-donut-body">
              {data?.subclasses
                ? <DrillDonut id="varc" subclasses={data.subclasses} drill={data.drill} mode="varc" theme={theme} drillKey={drillKey} onDrill={setDrillKey} />
                : !loading && <div className="trg-no-data">No data</div>}
            </div>
          </div>

        </div>{/* /alt-donut-row */}

        {/* ── Positions table ── */}
        <div className="trg-card trg-tool-host">
          <CardTools onCsv={handlePositionsCsv} onExpand={() => setPositionsExpandOpen(true)} />
          <div className="trg-card-header" style={{ paddingRight: "2.5rem" }}>
            <div className="trg-card-title">Alternative Positions</div>
          </div>
          {positionsTable}
        </div>
        <ExpandModal open={positionsExpandOpen} title="Alternative Positions"
          onClose={() => setPositionsExpandOpen(false)} onCsv={handlePositionsCsv}>
          {positionsTable}
        </ExpandModal>

      </main>

      {/* ── Fullscreen overlay ── */}
      {fsPanel && (
        <div className="trg-fs-overlay active" onClick={(e) => { if (e.target === e.currentTarget) closeFs(); }}>
          <div className="trg-fs-panel">
            <div className="trg-fs-header">
              <div className="trg-fs-title">{fsPanel.title}</div>
              <button className="trg-fs-close" onClick={closeFs}>✕ close</button>
            </div>
            <div className="trg-fs-body">{fsPanel.content}</div>
          </div>
        </div>
      )}

    </div>
  );
}
